/*
 * Product page pincode delivery checker (India).
 * Markup + config: snippets/pincode-checker.liquid. Styles: assets/cro-enhancements.css.
 */
if (!window.Eurus.loadedScript.has('pincode-checker.js')) {
  window.Eurus.loadedScript.add('pincode-checker.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      const STORAGE_KEY = 'jespara:pincode';
      const PINCODE_PATTERN = /^[1-9][0-9]{5}$/;
      const IST_OFFSET_MINUTES = 330;
      const lookupCache = new Map();

      const readStoredPincode = () => {
        try {
          return window.localStorage.getItem(STORAGE_KEY) || '';
        } catch (e) {
          return '';
        }
      };

      const storePincode = (pincode) => {
        try {
          window.localStorage.setItem(STORAGE_KEY, pincode);
        } catch (e) {
          // Storage can be unavailable (private mode, blocked site data); the checker still works.
        }
      };

      // Current wall-clock time in India, independent of the shopper's device timezone.
      const nowInIndia = () => {
        const now = new Date();
        return new Date(now.getTime() + (now.getTimezoneOffset() + IST_OFFSET_MINUTES) * 60000);
      };

      const isExcludedDay = (date, excludedWeekend) => {
        const day = date.getDay();
        if (day === 6 && excludedWeekend.includes('saturday')) return true;
        if (day === 0 && excludedWeekend.includes('sunday')) return true;
        return false;
      };

      // Adds `days` delivery days to `start`, skipping the store's non-delivery weekdays.
      const addDeliveryDays = (start, days, excludedWeekend) => {
        const date = new Date(start.getTime());
        let remaining = days;
        while (remaining > 0) {
          date.setDate(date.getDate() + 1);
          if (!isExcludedDay(date, excludedWeekend)) remaining -= 1;
        }
        return date;
      };

      const formatDate = (date) =>
        date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

      const lookupPlace = async (pincode) => {
        if (lookupCache.has(pincode)) return lookupCache.get(pincode);
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 4000);
        try {
          const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`, {
            signal: controller.signal,
          });
          if (!response.ok) return { status: 'unavailable' };
          const payload = await response.json();
          const entry = Array.isArray(payload) ? payload[0] : null;
          let result = { status: 'unavailable' };
          if (entry && entry.Status === 'Success' && Array.isArray(entry.PostOffice) && entry.PostOffice.length) {
            const office = entry.PostOffice[0];
            result = { status: 'found', district: office.District, state: office.State };
          } else if (entry && entry.Status === 'Error') {
            result = { status: 'not_found' };
          }
          lookupCache.set(pincode, result);
          return result;
        } catch (e) {
          return { status: 'unavailable' };
        } finally {
          clearTimeout(timeout);
        }
      };

      Alpine.data('xPincodeChecker', (sectionId) => ({
        pincode: '',
        state: 'idle',
        message: '',
        place: '',
        loading: false,
        config: null,
        init() {
          const configEl = document.getElementById(`cro-pincode-config-${sectionId}`);
          try {
            this.config = JSON.parse(configEl?.textContent || 'null');
          } catch (e) {
            this.config = null;
          }
          const stored = readStoredPincode();
          if (this.config && PINCODE_PATTERN.test(stored)) {
            this.pincode = stored;
            this.check();
          }
        },
        deliveryRange(pincode) {
          const config = this.config;
          let minDays = Number(config.minDays) || 0;
          let maxDays = Number(config.maxDays) || minDays;
          const fastPrefixes = (config.fastPrefixes || []).filter(Boolean);
          if (
            fastPrefixes.length &&
            Number(config.fastMaxDays) > 0 &&
            fastPrefixes.some((prefix) => pincode.startsWith(prefix))
          ) {
            minDays = Number(config.fastMinDays) || Number(config.fastMaxDays);
            maxDays = Number(config.fastMaxDays);
          }
          const excludedWeekend = config.excludedWeekend || 'none';
          const now = nowInIndia();
          const minutesNow = now.getHours() * 60 + now.getMinutes();
          const cutoffMinutes = (Number(config.cutoffHour) || 0) * 60 + (Number(config.cutoffMinute) || 0);
          if (minutesNow > cutoffMinutes) {
            minDays += 1;
            maxDays += 1;
          }
          return {
            start: addDeliveryDays(now, minDays, excludedWeekend),
            end: addDeliveryDays(now, Math.max(maxDays, minDays), excludedWeekend),
          };
        },
        async check() {
          if (!this.config) return;
          const pincode = String(this.pincode || '').trim();
          if (!PINCODE_PATTERN.test(pincode)) {
            this.state = 'error';
            this.message = this.config.messages.invalid;
            this.place = '';
            return;
          }
          this.loading = true;
          let place = '';
          if (this.config.lookupCity) {
            const lookup = await lookupPlace(pincode);
            if (lookup.status === 'not_found') {
              this.loading = false;
              this.state = 'error';
              this.message = this.config.messages.notFound;
              this.place = '';
              return;
            }
            if (lookup.status === 'found') {
              place = `${this.config.messages.location} ${lookup.district}, ${lookup.state}`;
            }
          }
          const { start, end } = this.deliveryRange(pincode);
          const sameDay = start.toDateString() === end.toDateString();
          this.message = this.config.messages.delivery
            .replace('[start]', formatDate(start))
            .replace(sameDay ? /\s*[–-]\s*\[end\]/ : '[end]', sameDay ? '' : formatDate(end));
          this.place = place;
          this.state = 'success';
          this.loading = false;
          storePincode(pincode);
        },
      }));
    });
  });
}
